// Quels clubs le joueur devrait travailler.
//
// La règle vit ici, et non dans l'écran qui l'affiche, parce que DEUX écrans la
// posent : le tableau de bord, qui marque les clubs à travailler dans ses
// statistiques, et le choix du club au moment de taper. Deux copies auraient
// fini par diverger — et rien n'entame la confiance dans un conseil comme le
// voir se contredire d'un écran à l'autre.

import type { Stats, StatsClub } from './training.ts';

/** Sous ce nombre de balles sur la fenêtre, on ne juge pas.
 *
 *  Un club sorti une fois pour trois mauvaises balles serait signalé comme un
 *  point faible alors qu'on n'en sait tout simplement rien. */
export const MIN_SHOTS_TO_JUDGE = 6;

/** Part du sac qu'on accepte de marquer.
 *
 *  Marquer la moitié du sac, c'est ne rien marquer : le joueur cherche un ou
 *  deux clubs à prendre maintenant, pas un classement. Une PART plutôt qu'un
 *  nombre fixe, parce qu'un sac de quatorze clubs et un demi-sac de six n'ont
 *  pas la même notion de « quelques-uns ».
 *
 *  Trente pour cent : quatre clubs sur quatorze, deux sur six. De quoi varier
 *  d'une séance à l'autre sans jamais transformer le conseil en inventaire. */
const PART_MARQUEE = 0.3;

/** Combien de clubs on s'autorise à marquer, pour un sac donné. */
function plafond(bagClubs: number): number {
    if (bagClubs <= 0) return 1;
    return Math.max(1, Math.round(bagClubs * PART_MARQUEE));
}

const share = (n: number, total: number): number =>
    total > 0 ? Math.round((n * 1000) / total) / 10 : 0;

/** Poids du « préjugé », en balles.
 *
 *  Vaut vingt balles jouées à la moyenne du joueur. C'est l'ordre de grandeur
 *  à partir duquel un taux cesse de pouvoir basculer sur une seule frappe. */
const PRIOR = 20;

/** Le taux d'un club, TEMPÉRÉ par le nombre de balles.
 *
 *  50 % sur deux balles ne vaut pas 40 % sur cent vingt : le premier est une
 *  pièce jetée deux fois, le second une mesure. Un classement sur les taux
 *  bruts hisse donc en tête les clubs à peine joués, qui n'ont rien à y faire.
 *
 *  On mélange donc chaque club avec la moyenne du joueur, comme s'il avait
 *  déjà tapé vingt balles à cette moyenne. Deux balles ne déplacent alors
 *  presque rien ; cent vingt écrasent le préjugé et le vrai niveau ressort.
 *  C'est un lissage additif, la forme la plus simple d'estimation bayésienne.
 *
 *  Le résultat ne s'AFFICHE jamais : le joueur lit son taux réel et son
 *  nombre de balles, deux chiffres qu'il peut vérifier. Le lissage ne sert
 *  qu'à ordonner — il répond à « lequel d'abord », pas à « combien ». */
export function temperedRate(center: number, shots: number, average: number): number {
    return ((center + (PRIOR * average) / 100) * 100) / (shots + PRIOR);
}

/** Les clubs qui centrent MOINS que la moyenne du joueur sur la même fenêtre,
 *  du plus faible au moins faible.
 *
 *  Le repère est le joueur lui-même, pas un barème : quelqu'un à 40 % de
 *  centrage n'a rien à faire d'une norme de professionnel, et ce qui
 *  l'intéresse est de savoir où il perd du terrain par rapport à son propre
 *  jeu. Rien n'est renvoyé tant que la fenêtre est vide — sans moyenne, il n'y
 *  a pas d'écart à la moyenne. */
export function clubsToWork(stats: Stats | null): StatsClub[] {
    if (!stats || stats.recent.shots === 0) return [];
    /* La moyenne NON ARRONDIE.
     *
     * `share` arrondit au dixième, et ce dixième suffisait à fausser la
     * comparaison. Le lissage est construit pour qu'un club joué exactement à
     * la moyenne du joueur retombe sur cette moyenne — c'est vrai à l'unité
     * près, pas à l'arrondi près. Six balles centrées sur neuf donnent 66,666
     * et un arrondi à 66,7 : le club se retrouvait « sous la moyenne » de un
     * centième, et l'écran annonçait un point faible là où il n'y a que du
     * calcul. Un joueur qui n'a sorti qu'un club le voyait systématiquement
     * marqué. */
    const average = (stats.recent.center * 100) / stats.recent.shots;
    // Tous les clubs joués, sans plancher de volume.
    //
    // Il y en avait un — six balles — hérité d'avant la pondération, et les
    // deux se combattaient : la pondération ATTÉNUE un petit échantillon, le
    // plancher l'EFFACE. Résultat, deux fers à 0 % sur trois balles
    // disparaissaient du classement, et l'application conseillait à la place
    // un fer plus joué mais MEILLEUR. Le conseil était faux, et pour une
    // raison invisible depuis l'écran.
    //
    // La pondération suffit : un club à 0 % sur trois balles remonte vers la
    // moyenne, mais reste devant un club médiocre sur quarante — ce qui est
    // exactement le classement qu'on veut.
    return stats.clubs
        .filter((c) => c.shots > 0 && temperedRate(c.center, c.shots, average) < average)
        .sort((a, b) => temperedRate(a.center, a.shots, average)
            - temperedRate(b.center, b.shots, average))
        .slice(0, plafond(stats.overview.bagClubs || stats.clubs.length));
}

/** Les clubs à proposer au joueur, faiblesse d'abord, plafonnés.
 *
 *  Deux raisons de marquer un club, et elles ne se valent pas : être mauvais
 *  avec, ou ne pas le connaître. La faiblesse avérée passe devant — c'est
 *  celle qui coûte des coups aujourd'hui — puis viennent les clubs à peine
 *  sortis, du moins joué au plus joué.
 *
 *  L'ensemble est borné à une part du sac. Sans ce plafond, un sac dont la
 *  plupart des clubs sont peu joués les voyait tous marqués : un écran
 *  entièrement souligné ne souligne rien. */
export function clubsToFocus(stats: Stats | null): StatsClub[] {
    if (!stats) return [];
    const max = plafond(stats.overview.bagClubs || stats.clubs.length);
    const faibles = clubsToWork(stats);
    const retenus = [...faibles];
    for (const c of clubsUnderPlayed(stats).sort((a, b) => a.shots - b.shots)) {
        if (retenus.length >= max) break;
        if (!retenus.some((x) => x.clubId === c.clubId)) retenus.push(c);
    }
    return retenus.slice(0, max);
}

/** Pourquoi un club est marqué : parce qu'il est faible, ou parce qu'on ne le
 *  connaît pas encore. `null` s'il n'est pas marqué du tout.
 *
 *  Le GESTE est le même dans les deux cas — sortez-le et tapez avec — et c'est
 *  la raison pour laquelle un seul libellé servait aux deux. Mais le libellé
 *  ne vit pas seul : il s'affiche à côté du taux. « À travailler · 100 % » se
 *  lit comme une contradiction, et l'écran de jeu allait jusqu'à écrire « vos
 *  dernières séances le placent sous votre moyenne » d'un club qui était au
 *  dessus, faute d'avoir assez de balles pour trancher.
 *
 *  Ce n'est donc pas une nuance de conseil, c'est une question d'exactitude :
 *  on ne peut pas reprocher à un club ce qu'on n'a pas mesuré. */
export function raisonDuFocus(
    clubId: string, stats: Stats | null,
): 'faible' | 'inconnu' | null {
    if (!clubsToFocus(stats).some((c) => c.clubId === clubId)) return null;
    return clubsToWork(stats).some((c) => c.clubId === clubId) ? 'faible' : 'inconnu';
}

/** Les clubs TROP PEU JOUÉS pour qu'on puisse dire quoi que ce soit d'eux. */
export function clubsUnderPlayed(stats: Stats | null): StatsClub[] {
    if (!stats) return [];
    return stats.clubs.filter((c) => c.shots > 0 && c.shots < MIN_SHOTS_TO_JUDGE);
}

/** Le taux de centrage d'un club sur la fenêtre, ou `null` s'il est trop peu
 *  joué pour qu'on en dise quoi que ce soit. */
export function clubRate(c: StatsClub): number | null {
    return c.shots >= MIN_SHOTS_TO_JUDGE ? share(c.center, c.shots) : null;
}
